import { Pricing, formatCents } from '../api/pricing';
import { AccountRole } from '../types/auth';

interface PriceBreakdownProps {
  pricing: Pricing;
  role: AccountRole;
}

/**
 * The itemized "sticker price + GST, less the current discount, equals total due"
 * breakdown for one role — shared by {@code RoleSelectionModal} and {@code
 * UpgradeModal} so both quote the same numbers the same way, matching exactly what
 * {@code RoleActivationService#selectRole} actually charges. When the person already
 * holds a cheaper active role and this one costs more, {@code amountDueCents} is a
 * prorated mid-term upgrade charge rather than the flat total shown above it — the
 * two deliberately won't add up in that case, so a note explains why.
 */
export function PriceBreakdown({ pricing, role }: PriceBreakdownProps) {
  const base = pricing.baseRoleActivationFeeCents[role];
  const amountDue = pricing.amountDueCents[role];
  const isProrated = pricing.prorated[role];

  return (
    <div className="text-sm mb-4">
      <p
        className="text-white font-bold text-lg mb-1"
        style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.03em' }}
      >
        Price
      </p>
      <p style={{ color: 'rgba(147,197,253,0.8)' }}>
        Yearly Subscription: <span className="text-white">{formatCents(base)}</span> + GST
      </p>
      <p style={{ color: 'rgba(147,197,253,0.8)' }}>
        Discount Rate: <span className="text-white">{pricing.discountPercent}%</span>
      </p>
      <p className="text-white font-medium mt-1">Total Due: {formatCents(amountDue)}</p>
      {isProrated && (
        <p className="text-xs mt-1" style={{ color: 'rgba(147,197,253,0.6)' }}>
          Prorated for the time left on your current plan — your renewal date doesn't change.
        </p>
      )}
    </div>
  );
}
